import React from "react";
import { useState } from "react";
import { CloudUpload } from "@mui/icons-material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format } from "date-fns";
import DateRangePicker from "rsuite/DateRangePicker";
import { DateRange } from "rsuite/esm/DateRangePicker";
import styles from "./index.module.scss";
import deleteIcon from "../../../assets/images/delete-icon.svg";
import { Controller } from "react-hook-form";
import { getCurrencySymbol } from "../../../utils/programUtils";
import { handleAWSFileUpload } from "../../../utils/commonFunctions";
import CustomTimePicker from "../../../common/components/CustomTimePicker";
import CustomDropDown from "../../../common/components/CustomDropDown";
import CustomVenueModal from "../../../common/components/CustomVenueModal"; // Adjust path if needed
import { validateFileTypeAndSize } from "../../../utils/fileUploadValidation";
import CustomDatePicker from "../../../common/components/CustomDatePicker";
import { checkToDisable } from "../../../utils/checkToDisable";

interface SubProgram {
  id: string;
  title: string;
  banner?: File | null;
  description: string;
  startDate?: Date | null;
  endDate?: Date | null;
  modeOfProgram: "online" | "offline" | "hybrid";
  venueAddress: string[];
  customVenue: string;
  isTravelRequired?: "yes" | "no";
  isResidential?: "yes" | "no";
  isPaymentRequired: "yes" | "no";
  currency: string;
  programFee: string;
  isHighlighted?: boolean;
  highlightPhase?: "fade-in" | "visible" | "fade-out";
  bannerImageUrl?: string; // Added this to the interface
  showCustomVenue?: boolean;
}

interface SubProgramCardProps {
  subProgram: SubProgram;
  index: number;
  control: any;
  watch: any;
  errors: any;
  trigger: (name?: string | string[]) => Promise<boolean>;
  setValue: (name: string, value: any) => void;
  onSubProgramChange: (
    subProgramId: string,
    field: keyof SubProgram,
    value: any,
  ) => void;
  onSubProgramVenueChange: (subProgramId: string, venues: string[]) => void;
  // Updated to accept URL string instead of File
  onSubProgramBannerUpload: (subProgramIndex: string, imageUrl: string) => void;
  bannerImageUrl: string | null;
  onDeleteSubProgram: (subProgramId: string) => void;
  onRemove: () => void; // Added missing onRemove prop
  currencyOptions: Array<{ value: string; label: string; symbol: string }>;
  venueOptions: string[];
  isDateWithinProgramRange: (date: Date) => boolean;
  dateRange: [Date | null, Date | null];
  setDateRange: (dateRange: [Date | null, Date | null]) => void;
  onDateChange: (startDate: Date | null, endDate: Date | null) => void;
  programTypeNameFromSource: string; // Optional prop to get program type name from source
  fieldlength: number; // Optional prop to get the field length
}

const SubProgramCard = ({
  subProgram,
  index,
  control,
  watch,
  errors,
  setValue,
  onRemove,
  onSubProgramChange,
  onSubProgramVenueChange,
  onSubProgramBannerUpload,
  bannerImageUrl,
  onDeleteSubProgram,
  currencyOptions,
  venueOptions,
  isDateWithinProgramRange,
  dateRange,
  setDateRange,
  onDateChange,
  trigger,
  programTypeNameFromSource,
  fieldlength,
}: SubProgramCardProps) => {
  const formValues = watch(`subPrograms.${index}`);
  const modeOfProgram = watch(`subPrograms.${index}.modeOfProgram`);
  const isPaymentRequired = watch(`subPrograms.${index}.isPaymentRequired`);



  const handleDateChange = (range: [Date | null, Date | null] | null) => {
    if (range && range.length === 2) {
      const [startDate, endDate] = range;

      // Update form values
      setValue(`subPrograms.${index}.startDate`, startDate);
      setValue(`subPrograms.${index}.endDate`, endDate);

      // Update local state if needed
      setDateRange && setDateRange([startDate, endDate]);

      // Call parent handler if provided
      onDateChange && onDateChange(startDate, endDate);
    }
  };

  // Use form state value first, then fallback to prop
  // const displayBannerUrl = formBannerImageUrl || bannerImageUrl;

  const [isCustomVenueModalOpen, setCustomVenueModalOpen] =
    React.useState(false);

  const handleAddCustomVenue = (venue: string) => {
    setValue(`subPrograms.${index}.venueAddress`, [venue]);
    setCustomVenueModalOpen(false);
  };

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div className={styles.formContainer}>
          <div className={styles.formContent}>
            <div className={styles.subProgramHeader}>
              <p>{formValues?.title}</p>
              {fieldlength > 1 && (
                <img
                  src={deleteIcon}
                  alt="delete"
                  onClick={onRemove}
                  className={styles.subSessionDeleteIcon}
                />
              )}
            </div>
            {/* Title Field */}
            <Controller
              name={`subPrograms.${index}.title`}
              control={control}
              render={({ field }) => (
                <div className={styles.formSection}>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>
                      What's this sub-program called?
                    </label>
                    <input
                      {...field}
                      type="text"
                      className={`${styles.inputField} ${errors?.title ? styles.inputError : ""}`}
                      placeholder="Enter sub-program name"
                      style={{ width: "400px" }}
                    />
                    {errors?.title && (
                      <span className={styles.errorText}>
                        {errors.title.message}
                      </span>
                    )}
                  </div>
                </div>
              )}
            />
       
            {/* Description */}
            <Controller
              name={`subPrograms.${index}.description`}
              control={control}
              render={({ field }) => (
                <div className={styles.formSection}>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>
                      Add description for the sub-program
                    </label>
                    <textarea
                      {...field}
                      className={styles.textareaField}
                      rows={4}
                      placeholder="What is this sub-program about?"
                    />
                  </div>
                </div>
              )}
            />
            <div className={styles.formSection}>
              <div className={styles.ProgramDiv} data-testid="register-div">
                <div
                  className={styles.programInputContainer}
                  data-testid="register-date-div"
                >
                  <p
                    className={styles.labelText}
                    data-testid="label-text-start-date"
                  >
                    Program start date & time
                  </p>
                  <div
                    className={styles.containerDateTime}
                    data-testid="program-start-date-time-inputs"
                  >
                    <div
                      className={styles.containerDate}
                      data-testid="form-fields-registrations"
                    >
                      <Controller
                        name={`subPrograms.${index}.programStartDate`}
                        control={control}
                        render={({ field }) => (
                          <CustomDatePicker
                            value={field.value}
                            futureDate={true}
                            maxDate={watch("endDate") || null}
                            minDate={watch("startDate") || null}
                            onChange={(date) => {
                              field.onChange(date);
                              trigger(`subPrograms.${index}.programStartDate`);
                              trigger(`subPrograms.${index}.programStartTime`);
                            }}
                            startDate={new Date()}
                            borderRight={true}
                            errorExist={
                              !!errors?.subPrograms?.[index]
                                ?.programStartDate ||
                              !!errors?.subPrograms?.[index]?.programStartTime
                            }
                            errorMessage={
                              errors?.subPrograms?.[index]?.programStartDate
                                ?.message
                            }
                            data-testid="custom-date-picker"
                            dataTestId="program-start-date"
                          />
                        )}
                      />
                    </div>
                    <div
                      className={styles.containerTime}
                      data-testid="form-fields"
                    >
                      <Controller
                        name={`subPrograms.${index}.programStartTime`}
                        control={control}
                        render={({ field }) => (
                          <CustomTimePicker
                            value={field.value}
                            errorExist={
                              !!errors?.subPrograms?.[index]
                                ?.programStartDate ||
                              !!errors?.subPrograms?.[index]?.programStartTime
                            }
                            onChange={(time: Date | null) => {
                              if (time && !isNaN(time.getTime())) {
                                setValue(
                                  `subPrograms.${index}.programStartTime`,
                                  time,
                                );
                              }
                              trigger(`subPrograms.${index}.programStartTime`);
                              trigger(`subPrograms.${index}.programStartDate`);
                            }}
                            data-testid="custom-time-picker"
                            dataTestid="program-start-time"
                          />
                        )}
                      />
                    </div>
                  </div>
                  {errors?.programStartDate && (
                    <i
                      className={styles.dateErrorMsg}
                      data-testid="error-msg-start-date"
                    >
                      {errors.programStartDate?.message}
                    </i>
                  )}
                  {errors?.programStartTime && !errors?.programStartDate && (
                    <i
                      className={styles.dateErrorMsg}
                      data-testid="error-msg-start-time"
                    >
                      {errors.programStartTime?.message}
                    </i>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.formSection}>
              <div className={styles.ProgramDiv} data-testid="end-div">
                <div
                  className={styles.programInputContainer}
                  data-testid="end-date-div"
                >
                  <p
                    className={styles.labelText}
                    data-testid="label-text-end-date"
                  >
                    Program end date & time
                  </p>
                  <div
                    className={styles.containerDateTime}
                    data-testid="program-end-date-time-inputs"
                  >
                    <div
                      className={styles.containerDate}
                      data-testid="form-fields-end"
                    >
                      <Controller
                        name={`subPrograms.${index}.programEndDate`}
                        control={control}
                        render={({ field }) => (
                          <CustomDatePicker
                            value={field.value}
                            futureDate={true}
                            minDate={
                              watch(`subPrograms.${index}.programStartDate`) ||
                              null
                            }
                            maxDate={watch(`endDate`) || null}
                            onChange={(date) => {
                              field.onChange(date);
                              trigger(`subPrograms.${index}.programEndDate`);
                              trigger(`subPrograms.${index}.programEndTime`);
                            }}
                            startDate={new Date()}
                            borderRight={true}
                            errorExist={
                              !!errors?.subPrograms?.[index]?.programEndDate ||
                              !!errors?.subPrograms?.[index]?.programEndTime
                            }
                            errorMessage={
                              errors?.subPrograms?.[index]?.programEndDate
                                ?.message
                            }
                            data-testid="custom-date-picker"
                            dataTestId="program-end-date"
                          />
                        )}
                      />
                    </div>
                    <div
                      className={styles.containerTime}
                      data-testid="form-fields"
                    >
                      <Controller
                        name={`subPrograms.${index}.programEndTime`}
                        control={control}
                        render={({ field }) => (
                          <CustomTimePicker
                            value={field.value}
                            errorExist={
                              !!errors?.subPrograms?.[index]?.programEndDate ||
                              !!errors?.subPrograms?.[index]?.programEndTime
                            }
                            onChange={(time: Date | null) => {
                              if (time && !isNaN(time.getTime())) {
                                setValue(
                                  `subPrograms.${index}.programEndTime`,
                                  time,
                                );
                              }
                              trigger(`subPrograms.${index}.programEndTime`);
                              trigger(`subPrograms.${index}.programEndDate`);
                            }}
                            data-testid="custom-time-picker"
                            dataTestid="program-end-time"
                          />
                        )}
                      />
                    </div>
                  </div>
                  {errors?.programEndDate && (
                    <i
                      className={styles.dateErrorMsg}
                      data-testid="error-msg-end-date"
                    >
                      {errors.programEndDate?.message}
                    </i>
                  )}
                  {errors?.programEndTime && !errors?.programEndDate && (
                    <i
                      className={styles.dateErrorMsg}
                      data-testid="error-msg-end-time"
                    >
                      {errors.programEndTime?.message}
                    </i>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.formSection}>
              <div
                className={styles.programDiv}
                data-testid="checkin-start-div"
              >
                <div
                  className={styles.programInputContainer}
                  data-testid="checkin-start-date-div"
                >
                  <p
                    className={styles.labelText}
                    data-testid="label-text-checkin-start-date"
                  >
                    Check-in starts date & time
                  </p>
                  <div
                    className={styles.containerDateTime}
                    data-testid="checkin-start-date-time-inputs"
                  >
                    <div
                      className={styles.containerDate}
                      data-testid="form-fields-checkin-start"
                    >
                      <Controller
                        name={`subPrograms.${index}.checkInStartDate`}
                        control={control}
                        render={({ field }) => (
                          <CustomDatePicker
                            value={field.value}
                            futureDate={true}
                            minDate={
                              watch(`subPrograms.${index}.programStartDate`) ||
                              null
                            }
                            maxDate={
                              watch(`subPrograms.${index}.programEndDate`) ||
                              null
                            }
                            onChange={(date) => {
                              field.onChange(date);
                              trigger(`subPrograms.${index}.checkInStartDate`);
                              trigger(`subPrograms.${index}.checkInStartTime`);
                            }}
                            startDate={new Date()}
                            borderRight={true}
                            errorExist={
                              !!errors?.subPrograms?.[index]
                                ?.checkInStartDate ||
                              !!errors?.subPrograms?.[index]?.checkInStartTime
                            }
                            errorMessage={
                              errors?.subPrograms?.[index]?.checkInStartDate
                                ?.message
                            }
                            data-testid="custom-date-picker"
                            dataTestId="checkin-start-date"
                          />
                        )}
                      />
                    </div>
                    <div
                      className={styles.containerTime}
                      data-testid="form-fields"
                    >
                      <Controller
                        name={`subPrograms.${index}.checkInStartTime`}
                        control={control}
                        render={({ field }) => (
                          <CustomTimePicker
                            value={field.value}
                            errorExist={
                              !!errors?.subPrograms?.[index]
                                ?.checkInStartDate ||
                              !!errors?.subPrograms?.[index]?.checkInStartTime
                            }
                            onChange={(time: Date | null) => {
                              if (time && !isNaN(time.getTime())) {
                                setValue(
                                  `subPrograms.${index}.checkInStartTime`,
                                  time,
                                );
                              }
                              trigger(`subPrograms.${index}.checkInStartTime`);
                              trigger(`subPrograms.${index}.checkInStartDate`);
                            }}
                            data-testid="custom-time-picker"
                            dataTestid="checkin-start-time"
                          />
                        )}
                      />
                    </div>
                  </div>
                  {errors?.checkInStartDate && (
                    <i
                      className={styles.dateErrorMsg}
                      data-testid="error-msg-checkin-start-date"
                    >
                      {errors.checkInStartDate?.message}
                    </i>
                  )}
                  {errors?.checkInStartTime && !errors?.checkInStartDate && (
                    <i
                      className={styles.dateErrorMsg}
                      data-testid="error-msg-checkin-start-time"
                    >
                      {errors.checkInStartTime?.message}
                    </i>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.formSection}>
              <div className={styles.programDiv} data-testid="checkin-end-div">
                <div
                  className={styles.programInputContainer}
                  data-testid="checkin-end-date-div"
                >
                  <p
                    className={styles.labelText}
                    data-testid="label-text-checkin-end-date"
                  >
                    Check-in ends date & time
                  </p>
                  <div
                    className={styles.containerDateTime}
                    data-testid="checkin-end-date-time-inputs"
                  >
                    <div
                      className={styles.containerDate}
                      data-testid="form-fields-checkin-end"
                    >
                      <Controller
                        name={`subPrograms.${index}.checkInEndDate`}
                        control={control}
                        render={({ field }) => (
                          <CustomDatePicker
                            value={field.value}
                            futureDate={true}
                            minDate={
                              watch(`subPrograms.${index}.checkInStartDate`) ||
                              null
                            }
                            maxDate={
                              watch(`subPrograms.${index}.programEndDate`) ||
                              null
                            }
                            onChange={(date) => {
                              field.onChange(date);
                              trigger(`subPrograms.${index}.checkInEndDate`);
                              trigger(`subPrograms.${index}.checkInEndTime`);
                            }}
                            startDate={new Date()}
                            borderRight={true}
                            errorExist={
                              !!errors?.subPrograms?.[index]?.checkInEndDate ||
                              !!errors?.subPrograms?.[index]?.checkInEndTime
                            }
                            errorMessage={
                              errors?.subPrograms?.[index]?.checkInEndDate
                                ?.message
                            }
                            data-testid="custom-date-picker"
                            dataTestId="checkin-end-date"
                          />
                        )}
                      />
                    </div>
                    <div
                      className={styles.programStartTime}
                      data-testid="form-fields"
                    >
                      <Controller
                        name={`subPrograms.${index}.checkInEndTime`}
                        control={control}
                        render={({ field }) => (
                          <CustomTimePicker
                            value={field.value}
                            errorExist={
                              !!errors?.subPrograms?.[index]?.checkInEndDate ||
                              !!errors?.subPrograms?.[index]?.checkInEndTime
                            }
                            onChange={(time: Date | null) => {
                              if (time && !isNaN(time.getTime())) {
                                setValue(
                                  `subPrograms.${index}.checkInEndTime`,
                                  time,
                                );
                              }
                              trigger(`subPrograms.${index}.checkInEndTime`);
                              trigger(`subPrograms.${index}.checkInEndDate`);
                            }}
                            data-testid="custom-time-picker"
                            dataTestid="checkin-end-time"
                          />
                        )}
                      />
                    </div>
                  </div>
                  {errors?.checkInEndDate && (
                    <i
                      className={styles.dateErrorMsg}
                      data-testid="error-msg-checkin-end-date"
                    >
                      {errors?.checkInEndDate?.message}
                    </i>
                  )}
                  {errors?.checkInEndTime && !errors?.checkInEndDate && (
                    <i
                      className={styles.dateErrorMsg}
                      data-testid="error-msg-checkin-end-time"
                    >
                      {errors?.checkInEndTime?.message}
                    </i>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.formSection}>
              <div
                className={styles.programDiv}
                data-testid="checkout-start-div"
              >
                <div
                  className={styles.programInputContainer}
                  data-testid="checkout-start-date-div"
                >
                  <p
                    className={styles.labelText}
                    data-testid="label-text-checkout-start-date"
                  >
                    Check-out starts date & time
                  </p>
                  <div
                    className={styles.containerDateTime}
                    data-testid="checkout-start-date-time-inputs"
                  >
                    <div
                      className={styles.containerDate}
                      data-testid="form-fields-checkout-start"
                    >
                      <Controller
                        name={`subPrograms.${index}.checkOutStartDate`}
                        control={control}
                        render={({ field }) => (
                          <CustomDatePicker
                            value={field.value}
                            futureDate={true}
                            minDate={
                              watch(`subPrograms.${index}.programEndDate`) ||
                              null
                            }
                            onChange={(date) => {
                              field.onChange(date);
                              trigger(`subPrograms.${index}.checkOutStartDate`);
                              trigger(`subPrograms.${index}.checkOutStartTime`);
                            }}
                            startDate={new Date()}
                            borderRight={true}
                            errorExist={
                              !!errors?.subPrograms?.[index]
                                ?.checkOutStartDate ||
                              !!errors?.subPrograms?.[index]?.checkOutStartTime
                            }
                            errorMessage={
                              errors?.subPrograms?.[index]?.checkOutStartDate
                                ?.message
                            }
                            data-testid="custom-date-picker"
                            dataTestId="checkout-start-date"
                          />
                        )}
                      />
                    </div>
                    <div
                      className={styles.containerTime}
                      data-testid="form-fields"
                    >
                      <Controller
                        name={`subPrograms.${index}.checkOutStartTime`}
                        control={control}
                        render={({ field }) => (
                          <CustomTimePicker
                            value={field.value}
                            errorExist={
                              !!errors?.subPrograms?.[index]
                                ?.checkOutStartDate ||
                              !!errors?.subPrograms?.[index]?.checkOutStartTime
                            }
                            onChange={(time: Date | null) => {
                              if (time && !isNaN(time.getTime())) {
                                setValue(
                                  `subPrograms.${index}.checkOutStartTime`,
                                  time,
                                );
                              }
                              trigger(`subPrograms.${index}.checkOutStartTime`);
                              trigger(`subPrograms.${index}.checkOutStartDate`);
                            }}
                            data-testid="custom-time-picker"
                            dataTestid="checkout-start-time"
                          />
                        )}
                      />
                    </div>
                  </div>
                  {errors?.checkOutStartDate && (
                    <i
                      className={styles.dateErrorMsg}
                      data-testid="error-msg-checkout-start-date"
                    >
                      {errors?.checkOutStartDate?.message}
                    </i>
                  )}
                  {errors?.checkOutStartTime && !errors?.checkOutStartDate && (
                    <i
                      className={styles.dateErrorMsg}
                      data-testid="error-msg-checkout-start-time"
                    >
                      {errors.checkOutStartTime?.message}
                    </i>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.formSection}>
              <div className={styles.programDiv} data-testid="checkout-end-div">
                <div
                  className={styles.programInputContainer}
                  data-testid="checkout-end-date-div"
                >
                  <p
                    className={styles.labelText}
                    data-testid="label-text-checkout-end-date"
                  >
                    Check-out ends date & time
                  </p>
                  <div
                    className={styles.containerDateTime}
                    data-testid="checkout-end-date-time-inputs"
                  >
                    <div
                      className={styles.containerDate}
                      data-testid="form-fields-checkout-end"
                    >
                      <Controller
                        name={`subPrograms.${index}.checkOutEndDate`}
                        control={control}
                        render={({ field }) => (
                          <CustomDatePicker
                            value={field.value}
                            futureDate={true}
                            minDate={
                              watch(`subPrograms.${index}.programEndDate`) ||
                              null
                            }
                            onChange={(date) => {
                              field.onChange(date);
                              trigger(`subPrograms.${index}.checkOutEndDate`);
                              trigger(`subPrograms.${index}.checkOutEndTime`);
                            }}
                            startDate={new Date()}
                            borderRight={true}
                            errorExist={
                              !!errors?.subPrograms?.[index]?.checkOutEndDate ||
                              !!errors?.subPrograms?.[index]?.checkOutEndTime
                            }
                            errorMessage={
                              errors?.subPrograms?.[index]?.checkOutEndDate
                                ?.message
                            }
                            data-testid="custom-date-picker"
                            dataTestId="checkout-end-date"
                          />
                        )}
                      />
                    </div>
                    <div
                      className={styles.programTime}
                      data-testid="form-fields"
                    >
                      <Controller
                        name={`subPrograms.${index}.checkOutEndTime`}
                        control={control}
                        render={({ field }) => (
                          <CustomTimePicker
                            value={field.value}
                            errorExist={
                              !!errors?.subPrograms?.[index]?.checkOutEndDate ||
                              !!errors?.subPrograms?.[index]?.checkOutEndTime
                            }
                            onChange={(time: Date | null) => {
                              if (time && !isNaN(time.getTime())) {
                                setValue(
                                  `subPrograms.${index}.checkOutEndTime`,
                                  time,
                                );
                              }
                              trigger(`subPrograms.${index}.checkOutEndTime`);
                              trigger(`subPrograms.${index}.checkOutEndDate`);
                            }}
                            data-testid="custom-time-picker"
                            dataTestid="checkout-end-time"
                          />
                        )}
                      />
                    </div>
                  </div>
                  {errors?.checkOutEndDate && (
                    <i
                      className={styles.dateErrorMsg}
                      data-testid="error-msg-checkout-end-date"
                    >
                      {errors.checkOutEndDate?.message}
                    </i>
                  )}
                  {errors?.checkOutEndTime && !errors?.checkOutEndDate && (
                    <i
                      className={styles.dateErrorMsg}
                      data-testid="error-msg-checkout-end-time"
                    >
                      {errors.checkOutEndTime?.message}
                    </i>
                  )}
                </div>
              </div>
              {/* Mode of Program */}
              <Controller
                name={`subPrograms.${index}.modeOfProgram`}
                control={control}
                render={({ field }) => (
                  <div className={styles.formSection}>
                    <div className={styles.fieldGroupMode}>
                      <div className={styles.venueField}>
                        <label className={styles.fieldLabel}>
                          What is mode of sub-program?
                        </label>
                        <div className={styles.radioGroup}>
                          {["online", "offline", "hybrid"].map((mode) => (
                            <label key={mode} className={styles.radioOption}>
                              <input
                                type="radio"
                                {...field}
                                value={mode}
                                className={styles.radioInput}
                                checked={field.value === mode}
                                onChange={() => {
                                  field.onChange(mode);
                                  // Reset isResendential and venueAddress when mode changes
                                  setValue(
                                    `subPrograms.${index}.isResendential`,
                                    undefined,
                                  );
                                  setValue(
                                    `subPrograms.${index}.venueAddress`,
                                    [],
                                  );
                                }}
                                disabled={checkToDisable(
                                  programTypeNameFromSource,
                                  `modeOfProgram-${mode}`,
                                )}
                              />
                              <span>
                                {mode.charAt(0).toUpperCase() + mode.slice(1)}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              />

              {watch(`subPrograms.${index}.modeOfProgram`) === "offline" && (
                <div className={styles.formSection}>
                  <div
                    className={styles.fieldGroupMode}
                    style={{
                      display: "flex",

                      alignItems: "flex-end",
                      alignContent: "center",
                      alignSelf: "center",
                    }}
                  >
                    {/* Is this residential program? */}
                    <div className={styles.venueField}>
                      <label className={styles.fieldLabel}>
                        Is this residential program?
                      </label>
                      <div className={styles.radioGroup}>
                        <Controller
                          name={`subPrograms.${index}.isResendential`}
                          control={control}
                          render={({ field }) => (
                            <>
                              <label className={styles.radioOption}>
                                <input
                                  type="radio"
                                  {...field}
                                  value="yes"
                                  checked={field.value === "yes"}
                                  className={styles.radioInput}
                                  onChange={() => field.onChange("yes")}
                                />
                                <span className={styles.radioLabel}>Yes</span>
                              </label>
                              <label className={styles.radioOption}>
                                <input
                                  type="radio"
                                  {...field}
                                  value="no"
                                  checked={field.value === "no"}
                                  className={styles.radioInput}
                                  onChange={() => field.onChange("no")}
                                  disabled={checkToDisable(
                                    programTypeNameFromSource,
                                    `isResendential-no`,
                                  )}
                                />
                                <span className={styles.radioLabel}>No</span>
                              </label>
                            </>
                          )}
                        />
                      </div>
                    </div>
                    {/* Venue Selection if residential is yes */}
                    {watch(`subPrograms.${index}.isResendential`) === "yes" && (
                      <Controller
                        name={`subPrograms.${index}.venueAddress`}
                        control={control}
                        render={({ field }) => (
                          <div className={styles.venueField}>
                            <label className={styles.fieldLabel}>
                              Where will the sub-program be held?
                            </label>

                            <CustomDropDown
                              // value={field.value?.[0] || ""}
                              value={
                                watch("venueAddress") &&
                                watch("venueAddress").length > 0
                                  ? venueOptions.find((venue) =>
                                      watch("venueAddress")[0]
                                        .toLowerCase()
                                        .includes(venue.toLowerCase()),
                                    ) || ""
                                  : ""
                              }
                              onChange={(val) => {
                                if (val === "Add Custom Venue") {
                                  setCustomVenueModalOpen(true);
                                } else {
                                  const newVenues = val ? [val] : [];
                                  field.onChange(newVenues);
                                  setValue(
                                    `subPrograms.${index}.venueAddress`,
                                    newVenues,
                                  );
                                }
                              }}
                              options={[
                                ...venueOptions.map((venue) => ({
                                  value: venue,
                                  label:
                                    venue.charAt(0).toUpperCase() +
                                    venue.slice(1),
                                })),
                              ]}
                              placeholder="Select the venue"
                              width={340}
                              height={40}
                              error={!!errors?.venueAddress}
                              className={styles.selectField}
                            />
                          </div>
                        )}
                      />
                    )}
                  </div>
                </div>
              )}
             
              {/* Payment Fields */}
              <div className={styles.formSection}>
                <div className={styles.fieldGroupMode}>
                  {/* Currency Selection */}
                  <Controller
                    name={`subPrograms.${index}.currency`}
                    control={control}
                    render={({ field }) => (
                      <div className={styles.venueField}>
                        <label className={styles.fieldLabel}>
                          Currency for the sub-program fee
                        </label>
                        <input
                          {...field}
                          type="text"
                          value="INR (₹)"
                          readOnly
                          className={styles.INRInputField} // Add styles as needed
                          disabled={true}
                        />
                      </div>
                    )}
                  />


                  <Controller
                    name={`subPrograms.${index}.programFee`}
                    control={control}
                    render={({ field }) => {
                      // Watch for changes in hdbFee
                      const hdbFee = watch("hdbFee");
                      const msdFee = watch("msdFee");

                      // Use useEffect to sync the value when hdbFee changes
                      React.useEffect(() => {
                        if (hdbFee && subProgram?.title?.includes("HDB")) {
                          // Convert to string to ensure full value is captured
                          const feeValue = String(hdbFee);
                          // Update the field value
                          setValue(`subPrograms.${index}.programFee`, feeValue);
                        }
                      }, [hdbFee]);

                      // Use useEffect to sync the value when msdFee changes
                      React.useEffect(() => {
                        if (msdFee && subProgram?.title?.includes("MSD")) {
                          // Convert to string to ensure full value is captured
                          const feeValue = String(msdFee);
                          // Update the field value
                          setValue(`subPrograms.${index}.programFee`, feeValue);
                        }
                      }, [msdFee]);

                      return (
                        <div className={styles.venueField}>
                          <label className={styles.fieldLabel}>
                            Enter sub-program fee
                          </label>
                          <div className={styles.currencyInput}>
                            <span className={styles.currencySymbol}>
                              {getCurrencySymbol(
                                watch(`subPrograms.${index}.currency`),
                              )}
                            </span>
                            <input
                              {...field}
                              type="number"
                              className={`${styles.inputField} ${errors?.programFee ? styles.inputError : ""}`}
                              id={styles.INRInputField}
                              placeholder="Enter Fee"
                              value={field.value || ""}
                              disabled={true} // Disable input if needed
                            />
                          </div>

                          {errors?.programFee?.message && (
                            <span className={styles.errorText}>
                              {errors.programFee.message}
                            </span>
                          )}
                        </div>
                      );
                    }}
                  />
                </div>
              </div>
              {/* )} */}
            </div>
          </div>
        </div>
      </LocalizationProvider>
      <CustomVenueModal
        isOpen={isCustomVenueModalOpen}
        onClose={() => setCustomVenueModalOpen(false)}
        onAdd={handleAddCustomVenue}
        title="Adding a Custom Venue"
        message="Enter the Venue Address"
        showInput={true}
      />
    </>
  );
};

export default SubProgramCard;
